/**
 * Sdílej QR do bankovní appky; fallback na stažení.
 * @param {HTMLImageElement} img  <img> s QR (může být i SVG)
 * @param {string} [spayd]        volitelně SPAYD řetězec ("SPD*1.0*ACC:...*")
 * @param {string} [basename='qr-platba'] název bez přípony
 */
export async function shareOrDownloadQr(img, spayd, basename = 'qr-platba') {
  const files = [];

  // 1) Připrav PNG z <img> (vč. SVG)
  const pngBlob = await imgToPngBlob(img);
  if (pngBlob) files.push(new File([pngBlob], `${basename}.png`, { type: 'image/png' }));

  // 2) Volitelně přidej .spayd soubor (banky umí SPAYD – ofiko formát QR Platby)
  // Specifikace: MIME application/x-shortpaymentdescriptor, přípona .spayd
  // https://qr-platba.cz/pro-vyvojare/specifikace-formatu/
  if (spayd) {
    const spaydBlob = new Blob([spayd], { type: 'application/x-shortpaymentdescriptor' });
    files.push(new File([spaydBlob], `${basename}.spayd`, { type: 'application/x-shortpaymentdescriptor' }));
  }

  // 3) Zkus nativní Sdílet (Web Share API + Files)
  try {
    if (navigator.share && files.length && (!navigator.canShare || navigator.canShare({ files }))) {
      await navigator.share({
        files,
        title: 'QR platba',
        text: spayd || undefined
      });
      return;
    }
  } catch (_) {
    console.log("WTF");
    /* uživatel zrušil / nepodporováno → spadneme na download */
  }

  // 4) Fallback: stáhni soubory
  for (const f of files) {
    const url = URL.createObjectURL(f);
    triggerDownload(url, f.name);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

async function imgToPngBlob(img) {
  if (!(img instanceof HTMLImageElement) || !img.src) return null;
  // pokus o fetch jako blob (funguje pro PNG/JPEG/WebP, i data:)
  try {
    const res = await fetch(img.src, { mode: 'cors' });
    const type = res.headers.get('content-type') || '';
    const blob = await res.blob();

    // když je to už PNG/JPEG/WebP → vrať rovnou
    if (/^image\/(png|jpe?g|webp)$/i.test(type) || /^data:image\//i.test(img.src)) return blob;

    // když je to SVG → vykresli na canvas a vrať PNG
    if (/^image\/svg\+xml$/i.test(type) || /\.svg(\?|#|$)/i.test(img.src)) {
      const svgText = await blob.text();
      return svgToPngBlob(svgText, img.naturalWidth || 1024, img.naturalHeight || 1024);
    }
  } catch (_) {
    // cross-origin bez CORS → zkus přímo načíst do <img> a kreslit (může selhat)
  }

  // univerzální cesta: načti do pomocného <img> a překresli
  const helper = new Image();
  helper.crossOrigin = 'anonymous';
  const load = new Promise((res, rej) => { helper.onload = () => res(); helper.onerror = rej; });
  helper.src = img.src;
  try {
    await load;
    const w = helper.naturalWidth || img.width || 1024;
    const h = helper.naturalHeight || img.height || 1024;
    return drawToPngBlob(helper, w, h);
  } catch {
    alert('Obrázek nejde zpracovat (CORS/SVG). Zvaž stejné doménové jméno nebo data URL.');
    return null;
  }
}

function svgToPngBlob(svgText, w = 1024, h = 1024) {
  const svgBlob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = async () => {
      URL.revokeObjectURL(url);
      resolve(await drawToPngBlob(img, w || img.width || 1024, h || img.height || 1024));
    };
    img.onerror = reject;
    img.src = url;
  });
}

function drawToPngBlob(img, w, h) {
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, w, h);
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
}

function triggerDownload(href, filename) {
  const a = document.createElement('a');
  a.href = href; a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}
